import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, Plus, Trash2, Pencil, Check, Lock, Loader2, Layers } from "lucide-react";

function TipiPicker({ tipi, selected, onToggle, testidPrefix }) {
  return (
    <div className="mt-2 rounded-md border border-[#1A2B4A]/12 bg-[#f9f8f5] p-2.5">
      <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5">
        Compare nei tipi di pratica
      </p>
      <div className="grid sm:grid-cols-2 gap-y-1 gap-x-3">
        {(tipi || []).map((t) => (
          <label key={t.id} className="flex items-center gap-2 text-[12.5px] text-[#1A2B4A] cursor-pointer">
            <input type="checkbox" data-testid={`${testidPrefix}-${t.id}`}
              checked={selected.includes(t.id)} onChange={() => onToggle(t.id)}
              className="accent-[#C9A84C]" />
            {t.nome}
          </label>
        ))}
        {(tipi || []).length === 0 && (
          <p className="text-xs text-[#4a5568]">Nessun tipo di pratica configurato: creane uno prima.</p>
        )}
      </div>
      <p className="text-[11px] text-[#4a5568] mt-2">
        Se non selezioni nessun tipo, il campo <b>non comparirà in nessuna pratica</b>: scegli almeno un tipo.
      </p>
    </div>
  );
}

export default function CampiAnagrafica() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["anagrafica-fields", ""], queryFn: async () => (await api.get("/anagrafica-fields")).data });
  const { data: tipiData } = useQuery({ queryKey: ["tipi-pratica"], queryFn: async () => (await api.get("/tipi-pratica")).data });
  const tipi = tipiData || [];
  const tipoNome = (id) => tipi.find((t) => t.id === id)?.nome || "tipo rimosso";
  const fields = data?.fields || [];
  const groups = (data?.groups || [])
    .map((g) => ({ ...g, fields: fields.filter((f) => (f.group || "altri") === g.key) }))
    .filter((g) => g.fields.length);

  const [label, setLabel] = useState("");
  const [newTipi, setNewTipi] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [tipiKey, setTipiKey] = useState(null);
  const [tipiSel, setTipiSel] = useState([]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["anagrafica-fields"] });
  const toggle = (list, setList) => (id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const add = async () => {
    if (!label.trim()) return toast.error("Inserisci il nome del campo");
    setBusy(true);
    try {
      await api.post("/anagrafica-fields", { label: label.trim(), tipi_pratica: newTipi });
      toast.success(newTipi.length
        ? `Campo aggiunto per ${newTipi.length} tipo/i di pratica`
        : "Campo aggiunto, ma non comparirà finché non gli assegni un tipo di pratica");
      setLabel(""); setNewTipi([]); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const saveRename = async (key) => {
    try { await api.patch(`/anagrafica-fields/${key}`, { label: editLabel.trim() }); toast.success("Campo rinominato"); setEditKey(null); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const saveTipi = async (key) => {
    try {
      await api.patch(`/anagrafica-fields/${key}`, { label: "", tipi_pratica: tipiSel });
      toast.success("Tipi di pratica aggiornati"); setTipiKey(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const remove = async (key) => {
    if (!window.confirm("Eliminare questo campo personalizzato? I valori già inseriti nei clienti resteranno salvati ma non saranno più visibili.")) return;
    try { await api.delete(`/anagrafica-fields/${key}`); toast.success("Campo eliminato"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="mt-6 bg-white border border-[#1A2B4A]/10 rounded-sm p-4" data-testid="campi-anagrafica">
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal className="w-5 h-5 text-[#1A2B4A]" />
        <h3 className="font-serif text-xl text-[#1A2B4A]">Campi anagrafica</h3>
      </div>
      <p className="text-sm text-[#4a5568] mb-4">
        I campi <b>predefiniti</b> compaiono sempre in ogni scheda cliente e in ogni pratica e non possono essere eliminati.
        I campi <b>personalizzati</b> (es. "Numero fondo", "CUD") compaiono <b>solo</b> nei tipi di pratica che gli assegni:
        crea prima il tipo di pratica, poi il campo. Un campo senza tipi assegnati non compare in nessuna pratica.
      </p>

      <div className="space-y-5 mb-5">
        {groups.map((g) => (
          <div key={g.key} data-testid={`field-group-${g.key}`}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#4a5568] mb-1.5 pb-1 border-b border-[#C9A84C]/40">
              {g.label}
              {g.key === "azienda" && <span className="normal-case tracking-normal font-normal text-[#8a6d1f]"> — solo per anagrafiche azienda</span>}
            </div>
            <div className="space-y-2">
              {g.fields.map((f) => (
                <div key={f.key} data-testid={`field-row-${f.key}`} className="border-b border-[#1A2B4A]/5 pb-2">
                  <div className="flex items-center gap-3">
                    {editKey === f.key ? (
                      <>
                        <input data-testid="field-edit-input" autoFocus value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveRename(f.key)}
                          className="flex-1 px-2 py-1.5 text-sm border border-[#C9A84C] rounded-sm focus:outline-none" />
                        <button data-testid="field-edit-save" onClick={() => saveRename(f.key)} className="text-[#276749]"><Check className="w-4 h-4" /></button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-[#1A2B4A]">{f.label}</span>
                        {f.builtin ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-[#4a5568]"><Lock className="w-3 h-3" /> predefinito</span>
                        ) : (
                          <span className="text-[11px] uppercase tracking-wide text-[#C9A84C]">personalizzato</span>
                        )}
                        {!f.builtin && (
                          <button data-testid={`field-tipi-${f.key}`} title="Scegli in quali tipi di pratica compare"
                            onClick={() => { setTipiKey(tipiKey === f.key ? null : f.key); setTipiSel(f.tipi_pratica || []); }}
                            className="p-1.5 rounded-md text-[#1A2B4A] hover:bg-[#f0ede4]"><Layers className="w-4 h-4" /></button>
                        )}
                        <button data-testid={`field-rename-${f.key}`} onClick={() => { setEditKey(f.key); setEditLabel(f.label); }} title="Rinomina"
                          className="p-1.5 rounded-md text-[#1A2B4A] hover:bg-[#f0ede4]"><Pencil className="w-4 h-4" /></button>
                        {!f.builtin && (
                          <button data-testid={`field-delete-${f.key}`} onClick={() => remove(f.key)} title="Elimina"
                            className="p-1.5 rounded-md text-[#9b2c2c] hover:bg-[#f7e9e9]"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </>
                    )}
                  </div>
                  {!f.builtin && (
                    <div data-testid={`field-tipi-label-${f.key}`}
                      className={`text-[11px] mt-1 ${(f.tipi_pratica || []).length === 0 ? "text-[#8a6d1f] font-medium" : "text-[#4a5568]"}`}>
                      {(f.tipi_pratica || []).length === 0
                        ? "Non assegnato: non compare in nessuna pratica — assegnalo a un tipo o eliminalo"
                        : `Compare in: ${(f.tipi_pratica || []).map(tipoNome).join(" · ")}`}
                    </div>
                  )}
                  {tipiKey === f.key && (
                    <div data-testid={`field-tipi-panel-${f.key}`}>
                      <TipiPicker tipi={tipi} selected={tipiSel} onToggle={toggle(tipiSel, setTipiSel)} testidPrefix={`field-tipo-${f.key}`} />
                      <div className="flex gap-2 mt-2">
                        <button data-testid={`field-tipi-save-${f.key}`} onClick={() => saveTipi(f.key)}
                          className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-sm hover:bg-[#22345c]">
                          <Check className="w-3.5 h-3.5" /> Salva
                        </button>
                        <button onClick={() => setTipiKey(null)} className="text-xs border border-[#1A2B4A]/15 px-3 py-1.5 rounded-sm hover:border-[#C9A84C]">Annulla</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-[#1A2B4A]/12 p-3" data-testid="new-field-box">
        <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-2">Nuovo campo personalizzato</p>
        <div className="flex gap-2">
          <input data-testid="new-field-label" value={label} onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nome nuovo campo (es. Numero fondo, CUD…)"
            className="flex-1 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
          <button data-testid="add-field-btn" onClick={add} disabled={busy}
            className="inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#22345c] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi
          </button>
        </div>
        <TipiPicker tipi={tipi} selected={newTipi} onToggle={toggle(newTipi, setNewTipi)} testidPrefix="new-field-tipo" />
      </div>
    </div>
  );
}
